//!#system	

//!#system	

<template>
	<v-card>
		<v-card-title>
			<span class="headline">Module Properties</span>
		</v-card-title>
		<v-divider />
		<v-progress-linear v-if="isLoading" indeterminate color="cyan"></v-progress-linear>
		<v-card-text>
			<div class="mt-2">
				<v-autocomplete
					v-model="model.bucket"
					:items="buckets"
					label="Choose the bucket"
					@change="bucketChanged"
					:loading="isLoading"
				></v-autocomplete>
			</div>
			<div v-if="!isLoading && model.bucket">
				<div v-if="detail.moduleType.type === 'Queues'">
					<v-switch
						v-model="model.moduleParams.isHighPriority"
						:label="queuesToggleLabel"
						:messages="queuesToggleMessage"
						@click="updateModel"
					/>
				</div>
				<div v-if="['Graph-line', 'Graph-bar'].includes(detail.moduleType.type)">
					<v-select
						v-model="model.moduleParams.filterBy.countBy"
						label="Count by..."
						:items="countByItems"
						@click="updateModel"
					/>
					<v-select
						v-model="model.moduleParams.filterBy.lookBackInteger"
						label="Look back number"
						:items="lookBackItems"
						@click="updateModel"
					/>
					<v-select
						v-model="model.moduleParams.filterBy.lookBackInterval"
						label="Look back interval"
						:items="lookBackIntervalItems"
						@click="updateModel"
					/>
				</div>
				<div v-if="['Chart-line', 'Chart-bar'].includes(detail.moduleType.type)">
					<v-select
						v-model="model.moduleParams.filterBy.countBy"
						label="Count by..."
						:items="countByItems"
						@click="updateModel"
					/>
					<v-select
						v-model="model.moduleParams.filterBy.lookBackInteger"
						label="Look back number"
						:items="lookBackItems"
						@click="updateModel"
					/>
					<v-select
						v-model="model.moduleParams.filterBy.lookBackInterval"
						label="Look back interval"
						:items="lookBackIntervalItems"
						@click="updateModel"
					/>
					<div v-if="model.moduleParams.filters.length === 0">
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" @click="addDataset()" class="mx-2" fab dark small color="teal">
									<v-icon dark small>mdi-plus</v-icon>
								</v-btn>
							</template>
							<span>Add another dataset</span>
						</v-tooltip>
					</div>
					<fieldset class="group" v-for="(filter, index) in model.moduleParams.filters" :key="filter.sig">
						<legend>Dataset {{index+1}}</legend>
						<v-select
							v-model="filter.savedFilter"
							label="Filter"
							:items="filters"
							@click="updateModel"
							item-text="filterCode"
							item-value="id"
							clearable
						/>
						<v-select
							v-model="filter.totalBy"
							label="Total by (default is count)"
							:items="['count', 'sum']"
							@click="updateModel"
						/>
						<v-select
							v-model="filter.totalCol"
							label="Count up (default is each item)"
							:items="countables"
							@click="updateModel"
						/>
						<v-select
							v-model="filter.dateCol"
							label="By date (default is created date)"
							:items="dateCompareables"
							@click="updateModel"
						/>
						<v-text-field
							v-model="filter.labelAs"
							label="Label the data as"
							@change="updateModel"
						/>
						<v-color-picker class="mt-2" hide-inputs v-model="filter.color"></v-color-picker>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" @click="addDataset()" class="mx-2" fab dark small color="teal">
									<v-icon dark small>mdi-plus</v-icon>
								</v-btn>
							</template>
							<span>Add another dataset</span>
						</v-tooltip>
						<v-tooltip bottom v-if="index > 0">
							<template v-slot:activator="{ on }">
								<v-btn v-on="on" @click="removeDataset(filter.sig)" class="mx-2" fab dark small color="warning">
									<v-icon dark small>mdi-minus</v-icon>
								</v-btn>
							</template>
							<span>Remove dataset {{index+1}} ({{filter.labelAs}})</span>
						</v-tooltip>
					</fieldset>
				</div>
				<div v-if="['Filter'].includes(detail.moduleType.type)">
					<v-select
						v-model="model.moduleParams.filter_id"
						label="Filter"
						:items="filters"
						@click="updateModel"
						item-text="filterCode"
						item-value="id"
					/>
				</div>
				<div v-if="['Kanban-board'].includes(detail.moduleType.type)">
					<div v-for="(column, columnIndex) in model.moduleParams.columns" :key="column.queueCode + '-kanban'">
						<v-select
							v-model="column.queueCode"
							:items="kanbanQueues(columnIndex)"
							:label="`Column ${columnIndex + 1} Queue Code`"
							clearable
							@click:clear="$delete(model.moduleParams.columns, columnIndex)"
						/>
					</div>
					<div class="mt-2"></div>
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-btn v-on="on" @click="addKanbanColumn" color="secondary" fab x-small dark>
								<v-icon dark x-small>mdi-plus</v-icon>
							</v-btn>
						</template>
						<span>Add a column to this board</span>
					</v-tooltip>
					<v-alert
						type="info"
						outlined
						dense
						class="mt-2"
					>
						If you want to prevent rules from running on "dequeue" triggers from this kanban board, configure your rule to have "kanban" be "false".
					</v-alert>
				</div>
				<div v-if="['Report'].includes(detail.moduleType.type)">
					<v-select
						v-model="model.moduleParams.reportDefinition"
						label="Report Definition"
						:items="reportDefinitions"
						@click="updateModel"
						item-text="reportCode"
						item-value="id"
					/>
				</div>
			</div>
		</v-card-text>
		<v-divider />
		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn text @click="closeDialog()">Exit</v-btn>
			<v-btn color="primary" :disabled="!canSubmit" @click="submit()">Save</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import apiService from '@/apiService';
import tools from '@/tools';
import series from 'to-series';

const baseKey = function(start, end) {
	return Math.floor(Math.random() * end) + start;
};

export default {
	name: 'ConfigureModule',
	components: {
	},
	props: {
		routeInfo: {
			type: Object
		},
		detail: { // Optional starter object
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			model: {
				bucket: '',
				moduleParams: {
					filterBy: {},
					filters: [],
					columns: []
				}
			},
			valid: false,
			errorState: false,
			errorMessage: '',
			isLoading: false,
			buckets: [],
			namespaces: {},
			countables: [''],
			dateCompareables: ['createdAt'],
			availableQueues: [],
			namespace: '',
			objectName: '',
			filters: null,
			reportDefinitions: []
		};
	},
	computed: {
		canSubmit() {
			if (this.detail.moduleType.type === 'Kanban-board') {
				if (this.model.moduleParams.columns.length == 0) return false;
				if (this.model.moduleParams.columns.find(c => !c.queueCode)) return false;
			}
			if (this.model.bucket) return true;
			return false;
		},
		objectList() {
			const list = [];
			Object.keys(this.namespaces).forEach(key => {
				this.namespaces[key].forEach(objectName => {
					list.push(key + '/' + objectName);
				});
			});
			return list;
		},
		queuesToggleMessage() {
			if (this.model.moduleParams.isHighPriority) return 'Show only high priority queues.';
			return 'Show only low priority queues.'
		},
		queuesToggleLabel() {
			if (this.model.moduleParams.isHighPriority) return 'High priority queues.';
			return 'Low priority queues.'
		},
		lookBackItems() {
			const ar = [];
			for (let i = 0; i < 300; i++) {
				ar.push(i);
			}
			return ar;
		},
		countByItems() {
			return ['Year', 'Month', 'Day', 'Hour'];
		},
		lookBackIntervalItems() {
			return ['Year', 'Month', 'Day', 'Hour'];
		}
	},
	methods: {
		defaults() {
			let type = this.detail.moduleType.type;
			if (typeof this.detail.moduleParams === 'object' && Object.keys(this.detail.moduleParams).length > 0) {
				// Handle legacy charts where you can only have 1 dataset
				// We can't copy the reference to `this.detail`, otherwise it'll break the model's mutation methods
				if (['Chart-line', 'Chart-bar'].includes(type) && !this.detail.moduleParams.filters) {
					this.model.bucket = this.detail.bucket;
					this.model.moduleParams.filterBy.countBy = this.detail.moduleParams.filterBy.countBy;
					this.model.moduleParams.filterBy.lookBackInterval = this.detail.moduleParams.filterBy.lookBackInterval;
					this.model.moduleParams.filterBy.lookBackInteger = this.detail.moduleParams.filterBy.lookBackInteger;
					this.model.moduleParams.filters.push({
						sig: 'filter-' + baseKey(1000, 6000),
						totalBy: this.detail.moduleParams.filterBy.totalBy || '',
						totalCol: this.detail.moduleParams.filterBy.totalCol || '',
						dateCol: this.detail.moduleParams.filterBy.dateCol || '',
						labelAs: this.detail.moduleParams.labelAs || '',
						color: this.detail.moduleParams.color
					});
				} else {
					this.model = this.detail;
				}
				return;
			}
			if (type === 'Queues') {
				this.model.moduleParams = {
					filterBy: {
						highPriority: false
					},
					isHighPriority: false
				};
				return;
			}
			if (['Graph-line', 'Graph-bar'].includes(type)) {
				this.model.moduleParams = {};
				this.model.moduleParams.filterBy = {};
				this.model.moduleParams.filterBy.countBy = 'Day';
				this.model.moduleParams.filterBy.lookBackInterval = 'Day';
				this.model.moduleParams.filterBy.lookBackInteger = 30;
				this.model.moduleParams.filterBy.totalBy = '';
				this.model.moduleParams.filterBy.dateCol = '';
				this.model.moduleParams.labelAs = 'Items';
				return;
			}
			if (['Chart-line', 'Chart-bar'].includes(type)) {
				this.model.moduleParams = {};
				this.model.moduleParams.filterBy = {};
				this.model.moduleParams.filterBy.countBy = 'Day';
				this.model.moduleParams.filterBy.lookBackInterval = 'Day';
				this.model.moduleParams.filterBy.lookBackInteger = 30;
				this.model.moduleParams.filters = []
				this.model.moduleParams.filters.push({
					sig: 'filter-' + baseKey(1000, 6000),
					totalBy: '',
					totalCol: '',
					dateCol: '',
					labelAs: 'Items',
					color: null
				});
				return;
			}
			if (['Filter'].includes(type)) {
				this.model.moduleParams = {};
			}
			if (['Kanban-board'].includes(type)) {
				this.model.moduleParams = {
					columns: []
				};
			}
			if (['Report'].includes(type)) {
				this.model.moduleParams = {
					reportDefinition: null
				};
			}
		},
		addDataset() {
			// Makes nested arrays reactive by using `this.$set(arrayToModify, indexOfNewVal, newVal)`
			this.$set(this.model.moduleParams.filters, this.model.moduleParams.filters.length, {
				sig: 'filter-' + baseKey(1000, 6000),
				totalBy: '',
				totalCol: '',
				dateCol: '',
				labelAs: 'Items',
				color: null
			});
		},
		removeDataset(sig) {
			this.model.moduleParams.filters = this.model.moduleParams.filters.filter(dataset => dataset.sig !== sig);
		},
		addKanbanColumn() {
			this.model.moduleParams.columns.push({
				queueCode: ''
			});
		},
		bucketChanged() {
			this.updateModel();
			let [ namespace, objectName ] = this.model.bucket.split('/');
			this.namespace = namespace;
			this.objectName = objectName;
			this.getBucketSpecificData();
		},
		getReportDefinitions(done) {
			if (this.detail.moduleType.type !== 'Report') return done();
			apiService.list({
				namespace: 'reporting',
				pluralName: 'reportDefinitions'
			}, (err, response) => {
				if (err) return done(err);
				this.reportDefinitions = response;
				return done();
			});
		},
		getBucketSpecificData(done) {
			let [ namespace, objectName ] = this.model.bucket.split('/');
			this.namespace = namespace;
			this.objectName = objectName;
			this.isLoading = true;
			series()
				.first(cb => {
					this.getDates(cb);	
				})
				.next(cb => {
					// TODO: Needs to be the bucket they choose!!!!
					this.getCountables(cb);
				})
				.next(cb => {
					apiService.getSavedFilters({ namespace: this.namespace, pluralName: this.objectName }, (err, results) => {
						if (err) return cb(err);
						if (!Array.isArray(results)) return cb();
						this.filters = results;
						return cb();
					});
				})
				.next(cb => {
					this.getAvailableQueues(cb);
				})
				.end(err => {
					if (err) {
						this.$store.dispatch('app/applyObjectError', {
							show: true,
							text: err 
						});
					}
					this.isLoading = false;
					if (typeof done === 'function') return done(err);
				});
		},
		getAvailableQueues(done) {
			apiService.availableQueues(tools.fromBucket(this.model.bucket), (err, queues) => {
				this.isLoading = false;
				if (err) return done(err);
				this.availableQueues = queues;
				return done();
			});
		},
		updateModel() {
			if (this.detail.moduleType.type === 'Queues') {
				this.model.moduleParams.filterBy = {
						highPriority: this.model.moduleParams.isHighPriority
				};
			}
		},
		getBuckets(done) {
			this.$store.dispatch('app/buckets', { 
				options: {}, 
				callback: (err, buckets) => {
					this.buckets = buckets;
					return done(err);
				} 
			});
		},
		getNamespaces(done) {
			apiService.action({
				...this.routeInfo,
				action: 'getNamespaces',
				method: 'get'
			}, (err, response) => {
				this.namespaces = response;
				if (done) return done(err);
			});
		},
		getCountables(done) {
			if (!this.namespace) return done();
			apiService.call({
				method: 'get',
				path: '/' +
					encodeURIComponent(tools.camelCaseToDash(this.namespace)) + '/' +
					encodeURIComponent(tools.camelCaseToDash(this.objectName)) +
					'/utility-routes/countables'
			}, (err, response) => {
				this.countables = response;
				this.countables.splice(0, 0, ''); // have an empty item at the beginning, which just counts
				if (done) return done(err);
			});
		},
		getDates(done) {
			if (!this.namespace) return done();
			apiService.call({
				method: 'get',
				path: '/' +
					encodeURIComponent(tools.camelCaseToDash(this.namespace)) + '/' +
					encodeURIComponent(tools.camelCaseToDash(this.objectName)) +
					'/utility-routes/date-compareables'
			}, (err, response) => {
				this.dateCompareables = response;
				if (done) return done(err);
			});
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					this.getBuckets(cb);	
				})
				.next(cb => {
					if (!this.model.bucket) return cb();
					this.getBucketSpecificData(cb);
				})
				.next(cb => {
					this.getReportDefinitions(cb);
				})
				.end(err => {
					if (err) {
						this.$store.dispatch('app/applyObjectError', {
							show: true,
							text: err 
						});
					}
					this.isLoading = false;
				});
		},
		closeDialog() {
			this.$emit('close');
			this.$emit('refresh');
		},
		submit() {
			// save the data
			apiService.call({
				method: 'post',
				path: '/' + encodeURIComponent(tools.camelCaseToDash(this.routeInfo.namespace)) + '/' + encodeURIComponent(tools.camelCaseToDash(this.routeInfo.pluralName)) + '/action/configure/' + encodeURIComponent(this.routeInfo.id),
				body: {
					moduleParams: this.model.moduleParams,
					bucket: this.model.bucket
				}
			}, (err) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: err 
					});
				} else {
					this.$store.dispatch('app/applyObjectUpdated', {
						show: true,
						text: 'Your dashboard module has been updated.'
					});
					this.$emit('refresh');
				}
			})
		},
		kanbanQueues(columnIndex) {
			let i = 0;
			const curQueues = [];
			this.model.moduleParams.columns.forEach(c => {
				if (columnIndex === i) return;
				curQueues.push(c.queueCode);
				i++;
			});
			return this.availableQueues.map(aq => aq.queueCode).filter(aq => !curQueues.includes(aq));
		}
	},
	mounted() {
	},
	created() {
		this.defaults();
		this.getInitialData();
	}
};
</script>

<style scoped>
.group {
	padding: 20px;
	border: 3px solid #eee;
	position: relative;
	margin-bottom: 20px;
}
.group legend {
	padding: 0 20px 0 20px;
}
</style>